import React, { useState, useEffect } from 'react';
import { Input } from '../Input';
import { INPUT_PLACEHOLDERS, KEYBOARD_KEYS } from '../../constants/common';
import { FieldValues, useForm } from 'react-hook-form';

type Numeric2 = string & { readonly __numeric2: unique symbol };

/**
 * This component is designed to be used WITH react-hook-form,
 *
 * Parent owns react-hook-form state compressedDate value
 * Parent passes update callbacks as props
 * Those callbacks internally call react-hook-form methods like:
 * register to update the value
 * because the <Input/> component uses the React Hook form to update
 * the component state that is passed through props
 */
interface ICompressedDateInputProps {
  id?: string;
  label?: string;
  name?: string;
  className?: string;
  initialValue?: string;
  errors?: string[];
  onInputChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onChange: (data: { month: Numeric2; year: Numeric2 }) => void;
}

export const CompressedDateInput: React.FC<ICompressedDateInputProps> = ({
  id = 'compressedDate',
  label = '',
  name = 'compressedDate',
  className = '',
  initialValue = '',
  errors: inputErrors = [],
  onInputChange,
  onBlur,
  onChange,
}) => {
  // Using react hook form to initilaize the value of compressedDate
  const {
    register,
    setValue,
    formState: { errors: rhfErrors },
  } = useForm<FieldValues>({
    defaultValues: { [name]: '' },
  });

  const [digits, setDigits] = useState('');

  // Ensures a string is exactly 2 numeric characters.
  const asNumeric2 = (value: string): Numeric2 | null => (/^\d{2}$/.test(value) ? (value as Numeric2) : null);

  // Formatting the numeric value in the MM/YY format
  const format = (rawDigits: string) => {
    if (rawDigits.length <= 2) return rawDigits;
    return `${rawDigits.slice(0, 2)}/${rawDigits.slice(2, 4)}`;
  };

  const unformat = (value: string) => value.replace(/\D/g, '');

  // Sync initial value into internal digit state and react-hook-form field state.
  useEffect(() => {
    if (initialValue) {
      const raw = initialValue.replace(/\D/g, '');
      setDigits(raw);
      setValue(name, format(raw));
    }
  }, [initialValue, name, setValue]);

  // Handles typing in the input field.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawDigits = unformat(e.target.value).slice(0, 4);
    const formatted = format(rawDigits);

    setDigits(rawDigits);
    setValue(name, formatted, { shouldValidate: true });
    // Emit partial value so parents can show inline validation before MM/YY is complete.
    onInputChange?.(formatted);

    if (rawDigits.length === 4) {
      const month = asNumeric2(rawDigits.slice(0, 2));
      const year = asNumeric2(rawDigits.slice(2, 4));

      if (month && year) {
        onChange({ month, year });
      }
    }
  };

  // Custom backspace behavior.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== KEYBOARD_KEYS.BACKSPACE) return;

    const input = event.currentTarget;
    const cursor = input.selectionStart ?? 0;

    // If cursor is right after slash "/" in: MM/|YY
    if (cursor === 3) {
      event.preventDefault();
      const newDigits = digits.slice(0, 1);
      const formatted = format(newDigits);
      setDigits(newDigits);
      setValue(name, formatted, { shouldValidate: true });
      onInputChange?.(formatted);
    }
  };

  return (
    <Input
      id={id}
      type="text"
      label={label}
      name={name}
      maxLength={5}
      placeholder={INPUT_PLACEHOLDERS.COMPRESSED_DATE_INPUT}
      value={format(digits)}
      className={`compressed-date-input ${className}`}
      errors={inputErrors}
      showErrorsWhileFocused
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={() => onBlur?.(format(digits))}
      varient="outlined"
      validateReset
      setValue={setValue}
      registerFn={register}
      errorsProps={rhfErrors as unknown as Record<string, { message: string; type: string }>}
      isErrorIcon={false}
    />
  );
};
